import { object, string } from 'yup';
import { parse } from 'date-fns';
import { injectable } from 'inversify';

import EpgService from '../EpgService';
import type { PlaylistItem } from '../../../types/playlist';
import type { EpgProgram } from '../../../types/epg';
import { logError, logWarn } from '../../logger';

const viewNexaEpgProgramSchema = object().shape({
  'episode-num': object().shape({
    '#text': string().required(),
  }),
  title: object().shape({
    '#text': string().required(),
  }),
  desc: object().shape({
    '#text': string(),
  }),
  icon: object().shape({
    src: string(),
  }),
  start: string().required(),
  stop: string().required(),
});

const parseData = (date: string): string => parse(date, 'yyyyMdHms xxxx', new Date()).toISOString();

@injectable()
export default class ViewNexaEpgService extends EpgService {
  /** View Nexa XMLTV feeds typically contain a couple of days of data. */
  epgDays = 2;

  transformProgram = async (data: unknown, item: PlaylistItem): Promise<EpgProgram> => {
    const program = await viewNexaEpgProgramSchema.validate(data);

    return {
      id: program['episode-num']['#text'],
      navigable: false,
      channelId: item.mediaid,
      title: program['title']['#text'],
      startTime: parseData(program['start']),
      endTime: parseData(program['stop']),
      description: program?.['desc']?.['#text'],
      cardImage: program?.['icon']?.['src'],
      cardImageSmall: program?.['icon']?.['src'],
      backgroundImage: program?.['icon']?.['src'],
    };
  };

  fetchSchedule = async (item: PlaylistItem): Promise<unknown[]> => {
    const { XMLParser } = await import('fast-xml-parser');

    const scheduleUrl = item.scheduleUrl;

    if (!scheduleUrl) {
      logWarn('ViewNexaEpgService', 'Tried requesting a schedule for an item with missing `scheduleUrl`', { item });
      return [];
    }

    const xmlParserOptions = {
      ignoreAttributes: false,
      attributeNamePrefix: '',
    };

    try {
      const data = await fetch(scheduleUrl).then((res) => res.text());
      const parser = new XMLParser(xmlParserOptions);
      const schedule = parser.parse(data);

      return schedule?.tv?.programme || [];
    } catch (error: unknown) {
      if (error instanceof Error) {
        logError('ViewNexaEpgService', `Fetch failed for View Nexa EPG schedule: '${scheduleUrl}'`, { error });
      }
    }

    return [];
  };

  fetchPrograms = async (channels: PlaylistItem[]) => {
    const result = await Promise.all(
      channels.map(async (channel) => {
        const schedule = await this.fetchSchedule(channel);
        const settled = await Promise.allSettled(schedule.map((program: unknown) => this.transformProgram(program, channel)));

        return settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      }),
    );

    return result.flat();
  };
}
